import { useMemo } from "react";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useSearchAgencies, useSearchClaims, useSearchPolicies } from "@/hooks/use-search";
import { useFavorites } from "@/lib/favorites";
import { useRecentSearches } from "@/lib/recent-searches";

export type PaletteResultKind = "policy" | "claim" | "agency";
export type PaletteResultSource = "favorite" | "recent" | "search";

// Favorites in the empty-state column are grouped by kind (Policy, then Claim,
// then Agency) rather than by pin time, so a mixed pin set reads predictably.
// Within a kind, newest-pinned wins.
const FAVORITE_KIND_ORDER: Record<PaletteResultKind, number> = {
  policy: 0,
  claim: 1,
  agency: 2,
};

export interface PaletteResultItem {
  kind: PaletteResultKind;
  id: string;
  label: string;
  source: PaletteResultSource;
}

export interface PaletteResults {
  // Flat list the input cursor navigates with arrow keys.
  results: PaletteResultItem[];
  // Only set in empty mode — split rows by source so the renderer can emit
  // headers ("Favorites" / "Recent") without re-walking the flat list. Indices
  // map back into `results` so keyboard nav stays correct across sections.
  sections: {
    favRows: { item: PaletteResultItem; index: number }[];
    recRows: { item: PaletteResultItem; index: number }[];
  } | null;
  hasQuery: boolean;
  isLoading: boolean;
  debounced: string;
}

// Two modes:
//  - Empty query: Favorites + Recent (deduped so a favorited row doesn't
//    appear twice).
//  - With query: Claims / Policies / Agencies, capped at 5 each. Claims first
//    because they're the most-used jump target.
export function usePaletteResults(query: string): PaletteResults {
  const debounced = useDebouncedValue(query, 200);
  const policies = useSearchPolicies(debounced);
  const claims = useSearchClaims(debounced);
  const agencies = useSearchAgencies(debounced);
  const favorites = useFavorites();
  const recent = useRecentSearches();

  const hasQuery = debounced.trim().length > 0;
  const isLoading = policies.isFetching || claims.isFetching || agencies.isFetching;

  const results = useMemo<PaletteResultItem[]>(() => {
    if (hasQuery) {
      const out: PaletteResultItem[] = [];
      for (const c of (claims.data ?? []).slice(0, 5)) {
        out.push({ kind: "claim", id: c.claimNumber, label: c.nameNumber, source: "search" });
      }
      for (const p of (policies.data ?? []).slice(0, 5)) {
        out.push({ kind: "policy", id: p.policyNumber, label: p.numberName, source: "search" });
      }
      for (const a of (agencies.data ?? []).slice(0, 5)) {
        out.push({ kind: "agency", id: a.agentNumber, label: a.nameNumber, source: "search" });
      }
      return out;
    }
    const out: PaletteResultItem[] = [];
    const sortedFavorites = [...favorites].sort(
      (a, b) => FAVORITE_KIND_ORDER[a.kind] - FAVORITE_KIND_ORDER[b.kind] || b.ts - a.ts,
    );
    for (const f of sortedFavorites.slice(0, 10)) {
      out.push({
        kind: f.kind,
        id: f.number,
        label: f.name ? `${f.number} — ${f.name}` : f.number,
        source: "favorite",
      });
    }
    // Recent reflects what you've actually opened, in time order. A recent that
    // happens to also be favorited still belongs here — the two columns answer
    // different questions ("pinned" vs "recently viewed"), so don't hide it.
    for (const r of recent.slice(0, 10)) {
      out.push({
        kind: r.kind,
        id: r.number,
        label: r.name ? `${r.number} — ${r.name}` : r.number,
        source: "recent",
      });
    }
    return out;
  }, [hasQuery, claims.data, policies.data, agencies.data, favorites, recent]);

  const sections = useMemo(() => {
    if (hasQuery) return null;
    const favRows: { item: PaletteResultItem; index: number }[] = [];
    const recRows: { item: PaletteResultItem; index: number }[] = [];
    results.forEach((item, index) => {
      if (item.source === "favorite") favRows.push({ item, index });
      else if (item.source === "recent") recRows.push({ item, index });
    });
    return { favRows, recRows };
  }, [hasQuery, results]);

  return { results, sections, hasQuery, isLoading, debounced };
}
